import {Controller} from "stimulus"

const CLOUDINARY_SCRIPT_SRC = "https://upload-widget.cloudinary.com/global/all.js"

// Set by Cms::Galleries::ImagesController on a 422 whose body is still the
// manager: the image change saved, the website just would not take the publish.
const PUBLISH_REJECTED_HEADER = "X-Cms-Gallery-Publish"

// Keys into the alert element's data attributes, where the copy lives.
const PUBLISH_ALERT = "publishMessage"
const FAILURE_ALERT = "failureMessage"
const WIDGET_ALERT = "widgetMessage"

// One shared load for the whole page. Two galleries connecting in the same
// turn must not each append the script, and — more importantly — the second
// one must not see the tag the first just appended and assume it has loaded.
let cloudinaryScriptLoad = null

function loadCloudinaryScript() {
  if (window.cloudinary) return Promise.resolve()
  if (cloudinaryScriptLoad) return cloudinaryScriptLoad

  cloudinaryScriptLoad = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = CLOUDINARY_SCRIPT_SRC
    script.type = "text/javascript"
    script.async = true
    script.onload = resolve
    script.onerror = () => {
      // Forget the failed load so the next click tries again rather than
      // leaving one transient CDN failure to disable uploads for the page.
      cloudinaryScriptLoad = null
      reject(new Error("Cloudinary upload widget failed to load"))
    }
    document.head.appendChild(script)
  })
  return cloudinaryScriptLoad
}

// Manages the image list of one CMS gallery block inside the page editor.
//
// Several galleries can sit on one page, so every lookup is scoped to
// this.element and the Cloudinary widget is held on the controller instance —
// the shared window.myWidget the graphics controller uses would have the
// second gallery uploading into the first.
//
// The image manager renders inside the page editor's form, so nothing here
// submits a form: each change is sent straight to Cms::Galleries::ImagesController.
export default class extends Controller {
  static targets = ["addButton"]

  initialize() {
    // Creates still in flight for the batch the widget is currently uploading.
    this.pendingCreates = []
    // What to tell the editor once the current interaction finishes, or null
    // when nothing went wrong.
    this.alertKey = null
  }

  connect() {
    this.prepareWidget()
  }

  disconnect() {
    // Closed before it is destroyed, and never the other way round. An open
    // widget holds the page's scroll — it saves document.body.style.overflow
    // and sets it to "hidden" — and only its close path hands that back.
    // destroy() removes the iframe first and shuts the message channel after,
    // so destroying an open widget strands the editor on a page that will not
    // scroll and puts Update Page, which sits below the block, out of reach.
    this.closeWidget()
    if (this.widget && typeof this.widget.destroy === "function") {
      this.widget.destroy()
    }
    this.widget = null
  }

  // Idempotent: the widget's close path is guarded by its own open flag, so
  // this does nothing to one the editor has already dismissed.
  closeWidget() {
    if (!this.widget || typeof this.widget.close !== "function") return

    // quiet, because this is not the editor dismissing the widget: the close
    // event would reach a controller that is on its way out.
    this.widget.close({quiet: true})
  }

  async openUploadWidget() {
    this.alertKey = null
    this.markBusy(true)

    try {
      await this.prepareWidget()
    } finally {
      // The block can be removed from the page editor while the script is
      // loading, which leaves no button to re-enable — markBusy tolerates that.
      this.markBusy(false)
    }

    if (this.widget) {
      this.widget.open()
    } else {
      this.note(WIDGET_ALERT)
    }
    this.reportOutcome()
  }

  // Loading the Cloudinary script is a network fetch, so the button says it is
  // working and refuses further clicks until it resolves. aria-busy carries the
  // same thing to assistive technology, which the disabled state alone does not.
  markBusy(busy) {
    if (!this.hasAddButtonTarget) return

    this.addButtonTarget.disabled = busy
    if (busy) {
      this.addButtonTarget.setAttribute("aria-busy", "true")
    } else {
      this.addButtonTarget.removeAttribute("aria-busy")
    }
  }

  async updateField(event) {
    const input = event.target
    const body = {image: {}}
    body.image[input.dataset.galleryField] = input.value

    this.alertKey = null
    // Deliberately no re-render: the editor is still in the row, and swapping
    // the list out from under a change event would move focus off the field
    // they just tabbed out of. The alert says what went wrong without it.
    await this.send({method: "PATCH", url: `${this.url}/${this.imageIdFor(input)}`, body})
    this.reportOutcome()
  }

  async delete(event) {
    if (!window.confirm("Are you sure you want to delete this image?")) return

    this.alertKey = null
    const html = await this.send({
      method: "DELETE",
      url: `${this.url}/${this.imageIdFor(event.target)}`
    })
    this.replaceWith(html)
    this.reportOutcome()
  }

  async refresh() {
    this.replaceWith(await this.send({method: "GET", url: this.url}))
  }

  // --- Cloudinary ---------------------------------------------------------

  async prepareWidget() {
    if (this.widget) return

    try {
      await loadCloudinaryScript()
    } catch (error) {
      console.error(error)
      return
    }

    // The block may have been removed from the page editor while the script
    // was still loading.
    if (this.widget || !this.element.isConnected || !window.cloudinary) return

    this.widget = window.cloudinary.createUploadWidget({
      cloudName: "govpossible-pbc",
      upload_preset: this.element.dataset.cloudinaryPreset,
      sources: ["local", "camera"],
      cropping: false,
      multiple: true
    }, (error, result) => this.onUpload(error, result))
  }

  async onUpload(error, result) {
    if (error || !result) return

    if (result.event === "success") {
      // Deliberately no re-render here. Cloudinary fires one success callback
      // per file without waiting, and this controller's element IS the manager
      // partial's root — so re-rendering now would disconnect the controller
      // mid-batch, destroying the widget while its queue is still uploading and
      // leaving every later callback writing into a detached element.
      this.pendingCreates.push(this.createImage(result.info))
    } else if (result.event === "queues-end") {
      // Every file has been sent; wait for the ones still in flight, then
      // re-read the list once so the rendered order matches the saved order.
      const creates = this.pendingCreates
      this.pendingCreates = []
      await Promise.all(creates)
      // The refresh replaces this controller's element, which disconnects it
      // and destroys the widget. Close it here, while it is still open and
      // still ours, so the page scroll it is holding comes back.
      this.closeWidget()
      await this.refresh()
      // Only now: the refresh re-renders the manager, which would wipe an alert
      // raised while the batch was still uploading.
      this.reportOutcome()
    }
  }

  // Deliberately sends no name. Cloudinary hands back original_filename, and
  // seeding the Name field with it published "Screenshot from 2026-09-21
  // 23-52-04" under the photo on the website. The label belongs to the editor:
  // left untyped, neither the page nor the lightbox shows anything for it.
  async createImage(info) {
    await this.send({
      method: "POST",
      url: this.url,
      body: {image: {stored_at: info.public_id}}
    })
  }

  // --- helpers ------------------------------------------------------------

  get url() {
    return this.data.get("url")
  }

  imageIdFor(element) {
    return element.closest("[data-gallery-image-id]").dataset.galleryImageId
  }

  async send({method, url, body}) {
    const options = {
      method: method,
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        "Content-Type": "application/json"
      }
    }
    if (body) options.body = JSON.stringify(body)

    const response = await fetch(url, options)

    // A refused publish answers 422 carrying the manager. The change itself is
    // saved, so that body is current and has to be rendered like any other.
    if (response.headers.get(PUBLISH_REJECTED_HEADER) === "rejected") {
      this.note(PUBLISH_ALERT)
      return response.text()
    }

    if (!response.ok) {
      console.error(`Gallery image request failed: ${method} ${url} (${response.status})`)
      this.note(FAILURE_ALERT)
      return null
    }
    return response.text()
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  // The manager partial's root element is this controller's element, so a
  // re-render replaces the element itself; Stimulus reconnects to the new one.
  replaceWith(html) {
    if (html === null) return

    this.element.outerHTML = html
  }

  // --- telling the editor -------------------------------------------------

  // A request that failed outright is worse news than one the website merely
  // would not publish, so a later refusal does not overwrite it.
  note(key) {
    if (this.alertKey !== FAILURE_ALERT) this.alertKey = key
  }

  // Looked up in the document rather than through this.element: a re-render
  // replaces the element this controller is attached to, so by the time this
  // runs this.element can be the node the manager used to be. Its attributes
  // survive the detachment, which is where the id comes from.
  reportOutcome() {
    const alert = document.getElementById(this.data.get("alert"))
    if (!alert) return

    if (this.alertKey) {
      alert.textContent = alert.dataset[this.alertKey]
      alert.classList.remove("hidden")
    } else {
      alert.textContent = ""
      alert.classList.add("hidden")
    }
  }
}
